import {
    Box,
    Text,
    Flex,
    IconButton,
    Tooltip,
    useBreakpointValue,
    AlertDialog,
    AlertDialogOverlay,
    AlertDialogContent,
    AlertDialogHeader,
    AlertDialogBody,
    AlertDialogFooter,
    Button,
    VStack,
    HStack,
    Divider,
    Badge,
    Icon,
    SimpleGrid,
    Spinner,
    Center,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    Checkbox,
    Collapse,
    Select,
    Input,
    InputGroup,
    InputLeftElement,
} from '@chakra-ui/react';
import { useDisclosure, CloseButton } from "@chakra-ui/react";
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import {
    FiArrowDown, FiArrowUp, FiTrash2, FiCalendar, FiUser, FiCreditCard,
    FiFileText, FiHash, FiDownload, FiChevronDown, FiRotateCcw, FiCheck,
    FiClock, FiChevronRight, FiChevronLeft, FiPhone, FiMail, FiSearch,
} from 'react-icons/fi';
import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import EventsService from "../../../../services/events/eventsService";
import ordersService from "../../../../services/orders/ordersService";
import { useTranslation } from "react-i18next";
import { useAppToast } from "../../../../hooks/useAppToast";
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { pdf } from '@react-pdf/renderer';
import InvoicePdf from './InvoicePdf';
import { useAuth } from "../../../../shared/providers/AuthProvider/AuthProvider";
import { BASE_URL } from "../../../../config";

const formatInvoiceData = (invoiceData) => {
    if (!invoiceData) return '—';
    let parsed = {};
    if (typeof invoiceData === 'object') {
        parsed = invoiceData;
    } else {
        try { parsed = JSON.parse(invoiceData); } catch { return invoiceData; }
    }
    const flat = {
        ...parsed,
        ...(typeof parsed.invoice === 'object' ? parsed.invoice : {}),
        ...(typeof parsed.company === 'object' ? parsed.company : {}),
        ...(typeof parsed.buyer === 'object' ? parsed.buyer : {})
    };
    const getVal = (keys) => {
        const key = Object.keys(flat).find(k => keys.includes(k.toLowerCase()));
        return key ? flat[key] : '';
    };
    const companyName = getVal(['companyname', 'company', 'nazwa_firmy', 'firm', 'nazwafirmy']);
    const nip = getVal(['nip', 'nipnumber', 'taxid', 'vatnumber']);
    const street = getVal(['address', 'street', 'ulica']);
    const building = getVal(['building', 'nr_budynku']);
    const apartment = getVal(['apartment', 'nr_lokalu']);
    const postalCode = getVal(['postalcode', 'zipcode', 'kod', 'kod_pocztowy', 'kodpocztowy']);
    const city = getVal(['city', 'miasto']);
    const addressParts = [street, building, apartment ? `/${apartment}` : '', (postalCode || city) ? ',' : '', postalCode, city]
        .filter(Boolean).join(' ').replace(/\s+,/, ',');
    const result = [companyName, nip ? `NIP: ${nip}` : '', addressParts].filter(Boolean).join(', ');
    return result || (typeof invoiceData === 'string' ? invoiceData : '—');
};

const isParticipantB2B = (participant) => {
    if (!participant.invoiceData) return false;
    const rawData = typeof participant.invoiceData === 'string' ? participant.invoiceData : JSON.stringify(participant.invoiceData);
    const lowerData = rawData.toLowerCase();
    return lowerData.includes('nip') || lowerData.includes('firma') || lowerData.includes('business') ||
        lowerData.includes('company') || lowerData.includes('nazwa_firmy');
};


const TicketsSubTable = ({ orderId, tickets, isLoading, onToggleAttended, onDownloadTicket, t }) => {
    if (isLoading) {
        return (
            <Flex justify="center" align="center" py={4} bg="#f9fafb">
                <Spinner size="sm" color="brand.500" mr={2} />
                <Text fontSize="13px" color="gray.500">{t('event.participants.loading', 'Wczytywanie...')}</Text>
            </Flex>
        );
    }

    if (tickets === null) {
        return (
            <Flex justify="center" align="center" gap={2} py={4} bg="#fff5f5">
                <Text fontSize="13px" color="red.500">{t('ticket.load_error', 'Nie udało się pobrać biletów.')}</Text>
            </Flex>
        );
    }

    if (!tickets || tickets.length === 0) {
        return (
            <Flex justify="center" py={4} bg="#f9fafb">
                <Text fontSize="13px" color="gray.500">{t('ticket.empty', 'Brak biletów')}</Text>
            </Flex>
        );
    }

    const subColumns = [
        t('ticket.th.lp', 'L.P.'),
        t('ticket.th.type', 'Rodzaj biletu'),
        t('ticket.th.price', 'Cena'),
        t('ticket.th.participant', 'Imię i nazwisko'),
        t('ticket.th.email', 'E-mail'),
        t('ticket.th.phone', 'Telefon'),
        t('ticket.th.attended', 'Czy uczestniczył'),
        t('ticket.th.download', 'Pobierz bilet'),
    ];

    return (
        <Box bg="#f0faf6" borderTop="2px solid" borderColor="brand.500" overflowX="auto">
            <Box minW="860px" px="8px" py="8px">
                <Box display="grid" gridTemplateColumns="40px 1.4fr 80px 1.6fr 1.4fr 1fr 130px 100px" gap="4px" mb="4px">
                    {subColumns.map((col, i) => (
                        <Box key={i} bg="brand.500" opacity={0.85} p="8px 10px" display="flex" alignItems="center">
                            <Text fontSize="12px" fontWeight="500" color="white" isTruncated title={col}>{col}</Text>
                        </Box>
                    ))}
                </Box>
                {tickets.map((ticket, idx) => {
                    const participantName = ticket.isPersonalized
                        ? [ticket.name, ticket.surName].filter(Boolean).join(' ') || '—'
                        : t('ticket.bearer', 'Bilet na okaziciela');
                    return (
                        <Box key={ticket.id} display="grid" gridTemplateColumns="40px 1.4fr 80px 1.6fr 1.4fr 1fr 130px 100px"
                            gap="4px" mb="4px" fontSize="13px">
                            <Box bg="white" p="8px 10px" display="flex" alignItems="center" justifyContent="center">
                                <Text color="gray.500">{idx + 1}</Text>
                            </Box>
                            <Box bg="white" p="8px 10px" display="flex" alignItems="center">
                                <Text isTruncated title={ticket.ticketTypeName}>{ticket.ticketTypeName || '—'}</Text>
                            </Box>
                            <Box bg="white" p="8px 10px" display="flex" alignItems="center">
                                <Text fontWeight="600">{ticket.unitGross != null ? `${Number(ticket.unitGross).toFixed(2)} zł` : '—'}</Text>
                            </Box>
                            <Box bg="white" p="8px 10px" display="flex" alignItems="center">
                                <Text isTruncated title={participantName} color={!ticket.isPersonalized ? "gray.400" : undefined} fontStyle={!ticket.isPersonalized ? "italic" : undefined}>
                                    {participantName}
                                </Text>
                            </Box>
                            <Box bg="white" p="8px 10px" display="flex" alignItems="center">
                                <Text isTruncated title={ticket.isPersonalized ? ticket.email : undefined}>{ticket.isPersonalized ? (ticket.email || '—') : '—'}</Text>
                            </Box>
                            <Box bg="white" p="8px 10px" display="flex" alignItems="center">
                                <Text isTruncated>{ticket.isPersonalized ? (ticket.phone || '—') : '—'}</Text>
                            </Box>
                            <Box bg="white" p="8px 10px" display="flex" alignItems="center" justifyContent="center">
                                <Checkbox isChecked={ticket.attended}
                                    onChange={() => onToggleAttended(orderId, ticket.id, ticket.attended)}
                                    size="md"
                                    sx={{
                                        '& .chakra-checkbox__control': { borderColor: '#089A7A' },
                                        '& .chakra-checkbox__control[data-checked]': { bg: '#089A7A', borderColor: '#089A7A' },
                                        '& .chakra-checkbox__control[data-checked] svg': { color: 'white', stroke: 'white' },
                                    }}
                                />
                            </Box>
                            <Box bg="white" p="8px 10px" display="flex" alignItems="center" justifyContent="center">
                                <Tooltip label={t('ticket.th.download', 'Pobierz bilet')}>
                                    <IconButton icon={<FiDownload />} aria-label={t('ticket.th.download', 'Pobierz bilet')}
                                        size="xs" variant="ghost" color="#0A4725" _hover={{ bg: 'brand.50' }}
                                        onClick={() => onDownloadTicket(ticket)} />
                                </Tooltip>
                            </Box>
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
};

const getKsefBadge = (status, t) => {
    switch (status) {
        case 'Success': return (
            <Badge colorScheme="green" display="inline-flex" alignItems="center" gap="4px" px={2} py={1} borderRadius="4px">
                <FiCheck size={11} style={{ strokeWidth: 3 }} />
                {t('ksef.status.success', 'KSeF: OK')}
            </Badge>
        );
        case 'Pending': return (
            <Badge colorScheme="yellow" display="inline-flex" alignItems="center" gap="4px" px={2} py={1} borderRadius="4px">
                <FiClock size={11} />
                {t('ksef.status.pending', 'W kolejce')}
            </Badge>
        );
        case 'Failed': return (
            <Badge colorScheme="red" display="inline-flex" alignItems="center" gap="4px" px={2} py={1} borderRadius="4px">
                {t('ksef.status.failed', 'Błąd')}
            </Badge>
        );
        default: return null;
    }
};

const MobileCard = ({
    participant,
    isExpanded,
    isLoadingT,
    tickets,
    isEditing,
    t,
    onRetryKsef,
    onToggleExpand,
    onGenerateInvoice,
    onToggleAttended,
    onDownloadTicket,
    onSelectParticipant,
    onOpenDelete,
}) => (
    <Box bg="white" borderRadius="12px" mb="12px" boxShadow="sm" border="1px solid" borderColor="gray.100" overflow="hidden">
        <Box p="16px">
            <Flex justify="space-between" align="start" mb="12px">
                <VStack align="start" spacing={1} flex="1">
                    <Text fontSize="xs" color="gray.500" fontWeight="bold">ID: {participant.participantId}</Text>
                    <Text fontSize="lg" fontWeight="700" color="#0A4725" wordBreak="normal" overflowWrap="anywhere">
                        {participant.fullName}
                    </Text>
                    {participant.buyerEmail && <Text fontSize="sm" color="gray.500">{participant.buyerEmail}</Text>}
                    {participant.buyerPhone && <Text fontSize="sm" color="gray.500">{participant.buyerPhone}</Text>}
                </VStack>
                <HStack>
                    {participant.ksefStatus === 'Failed' && (
                        <IconButton icon={<FiRotateCcw />} aria-label="Retry KSeF" size="sm" variant="ghost"
                            color="orange.500" onClick={() => onRetryKsef(participant.orderId)} />
                    )}
                    <IconButton icon={<FiTrash2 />} aria-label={t('action.delete')} size="sm" variant="ghost"
                        color="#DC0000" onClick={() => { onSelectParticipant(participant); onOpenDelete(); }} isDisabled={!isEditing} />
                </HStack>
            </Flex>

            <Divider mb="12px" />

            <SimpleGrid columns={2} spacing={4} mb="16px">
                <HStack spacing={2} align="start">
                    <Icon as={FiCalendar} color="gray.400" mt={1} />
                    <VStack align="start" spacing={0}>
                        <Text fontSize="xs" color="gray.500">{t('event.participants.th.date')}</Text>
                        <Text fontSize="sm">{moment(participant.registeredAt).format('D.MM.YYYY H:mm')}</Text>
                    </VStack>
                </HStack>
                <HStack spacing={2} align="start">
                    <Icon as={FiCreditCard} color="gray.400" mt={1} />
                    <VStack align="start" spacing={0}>
                        <Text fontSize="xs" color="gray.500">{t('event.participants.th.total_gross')}</Text>
                        <Text fontSize="sm" fontWeight="600">{(participant.totalGross || 0).toFixed(2)} zł</Text>
                    </VStack>
                </HStack>
                <HStack spacing={2} align="start">
                    <Icon as={FiFileText} color="gray.400" mt={1} />
                    <VStack align="start" spacing={0}>
                        <Text fontSize="xs" color="gray.500">{t('event.participants.th.ticket_count')}</Text>
                        <Text fontSize="sm">{participant.bearerTicketCount || 0}</Text>
                    </VStack>
                </HStack>
                <HStack spacing={2} align="start">
                    <Icon as={FiDownload} color="gray.400" mt={1} />
                    <VStack align="start" spacing={0} w="100%">
                        <Text fontSize="xs" color="gray.500">{t('event.participants.th.invoice')}</Text>
                        {isParticipantB2B(participant) ? (
                            <Text fontSize="sm">-</Text>
                        ) : (
                            <Button size="xs" variant="link" colorScheme="green" onClick={() => onGenerateInvoice(participant)}
                                fontWeight="600" justifyContent="flex-start" _hover={{ bg: 'transparent', textDecoration: 'underline' }}>
                                {t('event.participants.th.invoice_download', 'Pobierz fakturę')}
                            </Button>
                        )}
                    </VStack>
                </HStack>
                {isParticipantB2B(participant) && (
                    <HStack spacing={2} align="start">
                        <Icon as={FiCheck} color="gray.400" mt={1} />
                        <VStack align="start" spacing={0}>
                            <Text fontSize="xs" color="gray.500">{t('event.participants.th.ksef', 'Status KSeF')}</Text>
                            <HStack>
                                {getKsefBadge(participant.ksefStatus, t)}
                                {participant.ksefStatus === 'Failed' && (
                                    <IconButton icon={<FiRotateCcw />} aria-label="Retry KSeF" size="xs" variant="ghost"
                                        color="orange.500" onClick={() => onRetryKsef(participant.orderId)} />
                                )}
                            </HStack>
                        </VStack>
                    </HStack>
                )}
            </SimpleGrid>

            {participant.orderId && (
                <Button size="sm" variant="outline" colorScheme="brand" borderColor="#0A4725" color="#0A4725" w="100%"
                    rightIcon={<Icon as={isExpanded ? FiArrowDown : FiChevronRight} />}
                    onClick={() => onToggleExpand(participant.orderId)} isLoading={isLoadingT} mb={isExpanded ? 3 : 0}>
                    {isExpanded
                        ? t('ticket.hide', 'Ukryj bilety')
                        : t('ticket.show', 'Pokaż bilety') + (participant.bearerTicketCount ? ` (${participant.bearerTicketCount})` : '')}
                </Button>
            )}
        </Box>

        <Collapse in={isExpanded && !!participant.orderId} animateOpacity>
            <Box bg="#f0faf6" borderTop="2px solid" borderColor="brand.500" p="12px">
                {isLoadingT ? (
                    <Flex justify="center" py={3}><Spinner size="sm" color="brand.500" /></Flex>
                ) : tickets === null ? (
                    <Text fontSize="13px" color="red.500" textAlign="center">{t('ticket.load_error', 'Nie udało się pobrać biletów')}</Text>
                ) : (tickets || []).length === 0 ? (
                    <Text fontSize="13px" color="gray.500" textAlign="center">{t('ticket.empty', 'Brak biletów')}</Text>
                ) : (tickets || []).map((ticket, idx) => {
                    const participantName = ticket.isPersonalized
                        ? [ticket.name, ticket.surName].filter(Boolean).join(' ') || '—'
                        : t('ticket.bearer', 'Bilet na okaziciela');
                    return (
                        <Box key={ticket.id} bg="white" borderRadius="8px" p="12px" mb="8px" border="1px solid" borderColor="gray.100">
                            <Flex justify="space-between" align="center" mb="8px">
                                <Text fontSize="xs" color="gray.500" fontWeight="600">#{idx + 1} — {ticket.ticketTypeName || '—'}</Text>
                                <HStack>
                                    <Checkbox isChecked={ticket.attended} onChange={() => onToggleAttended(participant.orderId, ticket.id, ticket.attended)} size="sm"
                                        sx={{
                                            '& .chakra-checkbox__control': { borderColor: '#089A7A' },
                                            '& .chakra-checkbox__control[data-checked]': { bg: '#089A7A', borderColor: '#089A7A' },
                                            '& .chakra-checkbox__control[data-checked] svg': { color: 'white', stroke: 'white' },
                                        }}>
                                        <Text fontSize="xs">{t('ticket.th.attended', 'Uczestniczył')}</Text>
                                    </Checkbox>
                                    <IconButton icon={<FiDownload />} aria-label={t('ticket.th.download', 'Pobierz')} size="xs"
                                        variant="ghost" color="#0A4725" onClick={() => onDownloadTicket(ticket)} />
                                </HStack>
                            </Flex>
                            <Text fontSize="sm" fontWeight="500" color={!ticket.isPersonalized ? "gray.400" : undefined} fontStyle={!ticket.isPersonalized ? "italic" : undefined}>
                                {participantName}
                            </Text>
                            {ticket.isPersonalized && ticket.email && <Text fontSize="xs" color="gray.500">{ticket.email}</Text>}
                            {ticket.isPersonalized && ticket.phone && <Text fontSize="xs" color="gray.500">{ticket.phone}</Text>}
                            <Text fontSize="xs" color="brand.500" fontWeight="600" mt="4px">
                                {ticket.unitGross != null ? `${Number(ticket.unitGross).toFixed(2)} zł` : ''}
                            </Text>
                        </Box>
                    );
                })}
            </Box>
        </Collapse>
    </Box>
);

const EventParticipantsCardList = ({ eventId, isEditing, eventName, eventData }) => {
    const [sort, setSort] = useState('id');
    const [sortDir, setSortDir] = useState('desc');
    const [loading, setLoading] = useState(true);
    const [participants, setParticipants] = useState([]);
    const [selectedParticipant, setSelectedParticipant] = useState(null);

    // expand state
    const [expandedRows, setExpandedRows] = useState(new Set());
    const [ticketsCache, setTicketsCache] = useState({});
    const [loadingTickets, setLoadingTickets] = useState(new Set());

    // pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);

    // search
    const [search, setSearch] = useState('');

    const toast = useAppToast();
    const navigate = useNavigate();
    const { t } = useTranslation("default");
    const isMobile = useBreakpointValue({ base: true, md: false }, { fallback: 'base' });
    const { getSetup } = useAuth();

    const { isOpen, onOpen, onClose } = useDisclosure();
    const cancelRef = useRef();

    const handleSort = (column) => {
        setCurrentPage(1);
        if (sort === column) {
            setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        } else {
            setSort(column);
            setSortDir('asc');
        }
    };

    const fetchParticipants = async () => {
        setLoading(true);
        try {
            if (eventId) {
                const res = await EventsService.getEventParticipants(eventId);
                setParticipants(res.data.data);
            }
        } catch {
            toast({ title: t('request.error'), status: 'error', duration: 3000, isClosable: true });
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        try {
            await EventsService.deleteEventParticipant(eventId, selectedParticipant.id);
            toast({ title: t('event.participants.deleted'), status: 'success', duration: 3000, isClosable: true });
            fetchParticipants();
        } catch {
            toast({ title: t('event.participants.error.deleted'), status: 'error', duration: 3000, isClosable: true });
        } finally {
            onClose();
            setSelectedParticipant(null);
        }
    };

    const handleToggleExpand = useCallback(async (orderId) => {
        if (!orderId) return;
        const key = orderId.toString();

        setExpandedRows(prev => {
            const next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });

        // undefined = nie ładowano jeszcze, null = błąd, [] = puste, [...] = dane
        if (ticketsCache[key] === undefined) {
            setLoadingTickets(prev => new Set(prev).add(key));
            try {
                const res = await EventsService.getOrderTickets(orderId);
                setTicketsCache(prev => ({ ...prev, [key]: res.data.data ?? [] }));
            } catch {
                setTicketsCache(prev => ({ ...prev, [key]: null }));
                toast({ title: t('request.error'), status: 'error', duration: 3000, isClosable: true });
            } finally {
                setLoadingTickets(prev => { const s = new Set(prev); s.delete(key); return s; });
            }
        }
    }, [ticketsCache, t, toast]);

    const handleToggleAttended = useCallback(async (orderId, ticketId, currentAttended) => {
        const key = orderId.toString();
        const newAttended = !currentAttended;

        // optimistic update
        setTicketsCache(prev => ({
            ...prev,
            [key]: (prev[key] || []).map(t => t.id === ticketId ? { ...t, attended: newAttended } : t)
        }));

        try {
            await EventsService.markTicketAttended(ticketId, newAttended);
        } catch {
            // revert on failure
            setTicketsCache(prev => ({
                ...prev,
                [key]: (prev[key] || []).map(t => t.id === ticketId ? { ...t, attended: currentAttended } : t)
            }));
            toast({ title: t('ticket.attended_error', 'Błąd zapisu obecności'), status: 'error', duration: 3000, isClosable: true });
        }
    }, [t, toast]);

    const handleDownloadTicket = useCallback(async (ticket) => {
        try {
            const res = await ordersService.downloadTicket(ticket.id, ticket.downloadToken);
            const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = url;
            link.download = `bilet_${ticket.ticketNumber}.pdf`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(url);
        } catch {
            toast({ title: t('request.error'), status: 'error', duration: 3000, isClosable: true });
        }
    }, [t, toast]);

    const buildExportRows = () => {
        const header = [
            t('event.participants.export.columns.id', 'ID'),
            t('event.participants.export.columns.event_name', 'Nazwa wydarzenia'),
            t('event.participants.export.columns.participant_name', 'Imię i nazwisko uczestnika'),
            t('event.participants.export.columns.ticket_type', 'Rodzaj biletu'),
            t('event.participants.export.columns.amount_net', 'Kwota netto'),
            t('event.participants.export.columns.amount_gross', 'Kwota brutto'),
            t('event.participants.export.columns.buyer', 'Kto kupił bilet'),
            t('event.participants.export.columns.invoice_url', 'Link do faktury'),
            t('event.participants.export.columns.ticket_count', 'Ilość biletów'),
            t('event.participants.export.columns.invoice_data', 'Dane do faktury'),
        ];
        const rows = participants.map(p => [
            p.participantId ?? '—',
            eventName ?? '—',
            p.fullName ?? '—',
            p.ticketTypeName ?? '—',
            p.totalNetto != null ? Number(p.totalNetto).toFixed(2) : '—',
            p.totalGross != null ? Number(p.totalGross).toFixed(2) : '—',
            p.buyerName ?? (p.buyerEmail ?? '—'),
            p.ksefStatus === 'Success'
                ? 'Wysłana do KSeF'
                : (p.invoiceUrl
                    ? (p.invoiceUrl.startsWith('http') ? p.invoiceUrl : `${BASE_URL.replace(/\/$/, '')}/${p.invoiceUrl.replace(/^\//, '')}`)
                    : '—'),
            p.bearerTicketCount ?? 0,
            formatInvoiceData(p.invoiceData),
        ]);
        return [header, ...rows];
    };

    const sanitizeFilename = (name) => (name || 'uczestnicy').replace(/[^a-ząćęłńóśźżA-ZĄĆĘŁŃÓŚŹŻ0-9_-]/g, '_');

    const handleExport = (format) => {
        const data = buildExportRows();
        const ws = XLSX.utils.aoa_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Uczestnicy');
        const baseName = `uczestnicy_${sanitizeFilename(eventName)}_${moment().format('YYYY-MM-DD')}`;
        if (format === 'csv') {
            const csv = XLSX.utils.sheet_to_csv(ws, { FS: ";" });
            const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
            saveAs(blob, `${baseName}.csv`);
        } else if (format === 'xlsx') {
            const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
            saveAs(new Blob([buf], { type: 'application/octet-stream' }), `${baseName}.xlsx`);
        } else if (format === 'xls') {
            const buf = XLSX.write(wb, { bookType: 'xls', type: 'array' });
            saveAs(new Blob([buf], { type: 'application/vnd.ms-excel' }), `${baseName}.xls`);
        }
    };

    useEffect(() => { fetchParticipants(); }, [eventId]);

    const handleGenerateInvoice = async (participant) => {
        try {
            toast({ title: t('invoice.generating', 'Generowanie faktury...'), status: 'info', duration: 2000, isClosable: true });
            const setupResponse = await getSetup(eventData?.authorId);
            const authorSetup = setupResponse?.data || null;
            const blob = await pdf(<InvoicePdf participant={participant} eventName={eventName} eventData={eventData} authorSetup={authorSetup} />).toBlob();
            saveAs(blob, `faktura_${participant.participantId}_${moment().format('YYYYMMDD')}.pdf`);
        } catch {
            toast({ title: t('invoice.error', 'Wystąpił błąd podczas generowania faktury.'), status: 'error', duration: 3000, isClosable: true });
        }
    };

    const handleRetryKsef = async (orderId) => {
        try {
            await EventsService.retryKsefInvoice(orderId);
            toast({ title: t('ksef.retry.success', 'Zlecono ponowne wystawienie faktury'), status: 'success', duration: 3000, isClosable: true });
            fetchParticipants();
        } catch {
            toast({ title: t('ksef.retry.error', 'Błąd podczas zlecania ponownego wystawienia faktury'), status: 'error', duration: 3000, isClosable: true });
        }
    };


    // ----------------------------------------------------------------
    // Sorted + filtered list (must be before early returns — Rules of Hooks)
    // ----------------------------------------------------------------
    const sortedFilteredParticipants = useMemo(() => {
        const q = search.trim().toLowerCase();
        const filtered = q
            ? participants.filter(p =>
                (p.fullName || '').toLowerCase().includes(q) ||
                (p.buyerEmail || '').toLowerCase().includes(q) ||
                (p.buyerPhone || '').toLowerCase().includes(q)
            )
            : participants;

        return [...filtered].sort((a, b) => {
            let av, bv;
            switch (sort) {
                case 'id':          av = a.participantId ?? 0;             bv = b.participantId ?? 0;             break;
                case 'name':        av = (a.fullName || '').toLowerCase(); bv = (b.fullName || '').toLowerCase(); break;
                case 'date':        av = new Date(a.registeredAt);         bv = new Date(b.registeredAt);         break;
                case 'totalGross':  av = a.totalGross ?? 0;                bv = b.totalGross ?? 0;                break;
                case 'ticketCount': av = a.bearerTicketCount ?? 0;         bv = b.bearerTicketCount ?? 0;         break;
                case 'ksef': {
                    const order = { Success: 0, Pending: 1, Failed: 2 };
                    av = order[a.ksefStatus] ?? 3;
                    bv = order[b.ksefStatus] ?? 3;
                    break;
                }
                default:            return 0;
            }
            if (av < bv) return sortDir === 'asc' ? -1 : 1;
            if (av > bv) return sortDir === 'asc' ? 1 : -1;
            return 0;
        });
    }, [participants, search, sort, sortDir]);

    // ----------------------------------------------------------------
    // Loading / empty states
    // ----------------------------------------------------------------
    if (loading) {
        return (
            <Center py={10} w="100%">
                <VStack spacing={4}>
                    <Spinner size="xl" color="#0A4725" thickness="4px" />
                    <Text color="gray.500">{t('event.participants.loading', 'Wczytywanie uczestników...')}</Text>
                </VStack>
            </Center>
        );
    }

    if (participants.length === 0) {
        return (
            <Center py={10} w="100%" bg="white" borderRadius="8px" border="1px dashed" borderColor="gray.200">
                <VStack spacing={2}>
                    <Icon as={FiUser} boxSize={8} color="gray.300" />
                    <Text color="gray.500" fontWeight="medium">{t('event.participants.empty', 'Brak zarejestrowanych uczestników')}</Text>
                </VStack>
            </Center>
        );
    }

    // ----------------------------------------------------------------
    // Desktop column definitions
    // ----------------------------------------------------------------
    const columns = [
        { key: 'expand', label: '' },
        { key: 'id', label: t('event.participants.th.id') },
        { key: 'name', label: t('event.participants.th.name') },
        { key: 'email', label: t('event.participants.th.email', 'E-mail') },
        { key: 'phone', label: t('event.participants.th.phone', 'Telefon') },
        { key: 'date', label: t('event.participants.th.date') },
        { key: 'totalGross', label: t('event.participants.th.total_gross') },
        { key: 'ticketCount', label: t('event.participants.th.ticket_count') },
        { key: 'invoice', label: t('event.participants.th.invoice') },
        { key: 'ksef', label: t('event.participants.th.ksef', 'Status KSeF') },
        { key: 'actions', label: t('event.participants.th.actions') },
    ];

    const gridCols = "40px 60px 1fr 1fr 130px 130px 100px 70px 60px 120px 50px";

    const totalPages = Math.ceil(sortedFilteredParticipants.length / pageSize);
    const paginatedParticipants = sortedFilteredParticipants.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    return (
        <Box w="100%" maxW="7xl" mx="auto">
            {/* Toolbar: search + sort + export */}
            <Flex justify="space-between" align="center" mb="12px" px={{ base: 4, md: 0 }} gap={3} flexWrap="wrap">
                <InputGroup size="sm" maxW="260px" flex="1">
                    <InputLeftElement pointerEvents="none">
                        <Icon as={FiSearch} color="gray.400" fontSize="14px" />
                    </InputLeftElement>
                    <Input
                        placeholder="Szukaj po nazwisku, e-mail, telefonie..."
                        value={search}
                        onChange={e => { setSearch(e.target.value); setCurrentPage(1); }}
                        borderRadius="0"
                        borderColor="gray.200"
                        fontSize="13px"
                        _focus={{ borderColor: 'brand.500', boxShadow: 'none' }}
                        _placeholder={{ color: 'gray.400' }}
                    />
                </InputGroup>

                <HStack spacing={2} flexShrink={0}>
                    <Text fontSize="13px" color="gray.500" whiteSpace="nowrap">Sortuj:</Text>
                    <Select
                        size="sm"
                        value={sort}
                        onChange={e => { setSort(e.target.value); setCurrentPage(1); }}
                        borderRadius="0"
                        borderColor="gray.200"
                        bg="white"
                        fontSize="13px"
                        w="180px"
                        _focus={{ borderColor: 'brand.500', boxShadow: 'none' }}
                    >
                        <option value="id">ID</option>
                        <option value="name">Imię i nazwisko</option>
                        <option value="date">Data dołączenia</option>
                        <option value="totalGross">Kwota brutto</option>
                        <option value="ticketCount">Liczba biletów</option>
                        <option value="ksef">Status KSeF</option>
                    </Select>
                    <Tooltip label={sortDir === 'asc' ? 'Rosnąco' : 'Malejąco'}>
                        <IconButton
                            icon={<Icon as={sortDir === 'asc' ? FiArrowUp : FiArrowDown} />}
                            size="sm"
                            aria-label="Zmień kierunek sortowania"
                            onClick={() => { setSortDir(d => d === 'asc' ? 'desc' : 'asc'); setCurrentPage(1); }}
                            borderRadius="0"
                            bg="brand.500"
                            color="white"
                            _hover={{ bg: 'brand.600' }}
                            _active={{ bg: 'brand.600' }}
                        />
                    </Tooltip>

                    <Menu>
                        <MenuButton as={Button} rightIcon={<FiChevronDown />} leftIcon={<FiDownload />} size="sm"
                            bg="brand.500" color="white" borderRadius="0" _hover={{ bg: 'brand.600' }} _active={{ bg: 'brand.600' }}
                            fontWeight="500" fontSize="13px" px={4}>
                            {t('event.participants.export.button', 'Eksportuj')}
                        </MenuButton>
                        <MenuList minW="160px" zIndex={10} borderRadius="0" boxShadow="md">
                            <MenuItem fontSize="13px" onClick={() => handleExport('csv')} _hover={{ bg: '#f0faf4' }}>
                                {t('event.participants.export.csv', 'Eksportuj CSV')}
                            </MenuItem>
                            <MenuItem fontSize="13px" onClick={() => handleExport('xlsx')} _hover={{ bg: '#f0faf4' }}>
                                {t('event.participants.export.xlsx', 'Eksportuj XLSX')}
                            </MenuItem>
                            <MenuItem fontSize="13px" onClick={() => handleExport('xls')} _hover={{ bg: '#f0faf4' }}>
                                {t('event.participants.export.xls', 'Eksportuj XLS')}
                            </MenuItem>
                        </MenuList>
                    </Menu>
                </HStack>
            </Flex>

            {isMobile ? (
                <Box p="4">
                    {paginatedParticipants.map(participant => {
                        const mKey = participant.orderId?.toString();
                        return (
                            <MobileCard
                                key={participant.participantId}
                                participant={participant}
                                isExpanded={expandedRows.has(mKey)}
                                isLoadingT={loadingTickets.has(mKey)}
                                tickets={ticketsCache[mKey]}
                                isEditing={isEditing}
                                t={t}
                                onRetryKsef={handleRetryKsef}
                                onToggleExpand={handleToggleExpand}
                                onGenerateInvoice={handleGenerateInvoice}
                                onToggleAttended={handleToggleAttended}
                                onDownloadTicket={handleDownloadTicket}
                                onSelectParticipant={setSelectedParticipant}
                                onOpenDelete={onOpen}
                            />
                        );
                    })}
                </Box>
            ) : (
                <Box overflowX="auto" pb="4">
                    <Box minW="1100px">
                        <Box
                            display="grid"
                            gridTemplateColumns={gridCols}
                            columnGap="4px"
                            fontFamily="Inter"
                            fontSize="13px"
                            fontWeight="400"
                        >
                            {/* Header cells */}
                            {columns.map(col => (
                                <Box key={col.key} bg="#089A7A" borderRadius="0px" p="12px" display="flex" alignItems="center"
                                    color="white" boxShadow="sm" overflow="hidden">
                                    <Text isTruncated title={col.label} fontSize="13px">{col.label}</Text>
                                </Box>
                            ))}

                            {/* Spacer after header */}
                            <Box gridColumn="1 / -1" h="8px" />

                            {/* Data rows — all cells are direct children of the single grid */}
                            {paginatedParticipants.flatMap(participant => {
                                const key = participant.orderId?.toString();
                                const isExpanded = expandedRows.has(key);
                                const rowBg = isExpanded ? 'brand.50' : 'white';
                                const pid = participant.participantId;

                                return [
                                    <Box key={`${pid}-0`} bg={rowBg} p="8px" display="flex" alignItems="center" justifyContent="center"
                                        boxShadow={isExpanded ? 'inset 3px 0 0 #089A7A' : 'none'}>
                                        {participant.orderId && (
                                            <IconButton
                                                icon={<Icon as={isExpanded ? FiChevronDown : FiChevronRight} />}
                                                aria-label="Rozwiń bilety"
                                                size="xs"
                                                variant="ghost"
                                                color="#0A4725"
                                                onClick={() => handleToggleExpand(participant.orderId)}
                                                isLoading={loadingTickets.has(key)}
                                            />
                                        )}
                                    </Box>,
                                    <Box key={`${pid}-1`} bg={rowBg} p="12px" display="flex" alignItems="center">
                                        <Text>{participant.participantId}</Text>
                                    </Box>,
                                    <Box key={`${pid}-2`} bg={rowBg} p="12px" display="flex" alignItems="center">
                                        <Text fontWeight="500" wordBreak="break-word" whiteSpace="normal">{participant.fullName}</Text>
                                    </Box>,
                                    <Box key={`${pid}-3`} bg={rowBg} p="12px" display="flex" alignItems="center">
                                        <Text isTruncated title={participant.buyerEmail} color="gray.600">{participant.buyerEmail || '—'}</Text>
                                    </Box>,
                                    <Box key={`${pid}-4`} bg={rowBg} p="12px" display="flex" alignItems="center">
                                        <Text isTruncated>{participant.buyerPhone || '—'}</Text>
                                    </Box>,
                                    <Box key={`${pid}-5`} bg={rowBg} p="12px" display="flex" alignItems="center">
                                        <Text whiteSpace="nowrap">{moment(participant.registeredAt).format('D.MM.YYYY H:mm')}</Text>
                                    </Box>,
                                    <Box key={`${pid}-6`} bg={rowBg} p="12px" display="flex" alignItems="center">
                                        <Text fontWeight="600">{(participant.totalGross || 0).toFixed(2)} zł</Text>
                                    </Box>,
                                    <Box key={`${pid}-7`} bg={rowBg} p="12px" display="flex" alignItems="center" justifyContent="center">
                                        <Text textAlign="center">{participant.bearerTicketCount || 0}</Text>
                                    </Box>,
                                    <Box key={`${pid}-8`} bg={rowBg} p="12px" display="flex" alignItems="center" justifyContent="center">
                                        {isParticipantB2B(participant) ? (
                                            <Text>-</Text>
                                        ) : (
                                            <Tooltip label={t('event.participants.th.invoice_download', 'Pobierz fakturę')}>
                                                <IconButton icon={<FiDownload size={14} />} aria-label={t('event.participants.th.invoice_download', 'Pobierz fakturę')}
                                                    size="sm" variant="ghost" color="#0A4725" _hover={{ bg: 'brand.50' }}
                                                    onClick={() => handleGenerateInvoice(participant)} />
                                            </Tooltip>
                                        )}
                                    </Box>,
                                    <Box key={`${pid}-9`} bg={rowBg} p="12px" display="flex" alignItems="center" justifyContent="center" gap={2}>
                                        {isParticipantB2B(participant) ? (
                                            <>
                                                {getKsefBadge(participant.ksefStatus, t)}
                                                {participant.ksefStatus === 'Failed' && (
                                                    <Tooltip label={t('ksef.retry.button', 'Ponów wysyłkę')}>
                                                        <IconButton icon={<FiRotateCcw />} aria-label="Retry KSeF" size="xs"
                                                            variant="ghost" color="orange.500" onClick={() => handleRetryKsef(participant.orderId)} />
                                                    </Tooltip>
                                                )}
                                            </>
                                        ) : (
                                            <Text>-</Text>
                                        )}
                                    </Box>,
                                    <Box key={`${pid}-10`} bg={rowBg} p="8px" display="flex" justifyContent="center" gap={2} alignItems="center">
                                        <Tooltip label={t('action.delete')}>
                                            <IconButton icon={<FiTrash2 />} aria-label={t('action.delete')} size="sm"
                                                onClick={() => { setSelectedParticipant(participant); onOpen(); }}
                                                bg={rowBg} color="#DC0000" isDisabled={!isEditing} _hover={{ bg: 'red.50' }} />
                                        </Tooltip>
                                    </Box>,

                                    ...(isExpanded && participant.orderId ? [
                                        <Box key={`${pid}-subtable`} gridColumn="1 / -1">
                                            <TicketsSubTable
                                                orderId={participant.orderId}
                                                tickets={ticketsCache[key]}
                                                isLoading={loadingTickets.has(key)}
                                                onToggleAttended={handleToggleAttended}
                                                onDownloadTicket={handleDownloadTicket}
                                                t={t}
                                            />
                                        </Box>,
                                    ] : []),

                                    <Box key={`${pid}-spacer`} gridColumn="1 / -1" h="4px" />,
                                ];
                            })}
                        </Box>
                    </Box>
                </Box>
            )}

            {/* Pagination */}
            {sortedFilteredParticipants.length > 0 && (
                <Flex align="center" justify="space-between" mt="16px" px={{ base: 4, md: 0 }} flexWrap="wrap" gap={3}>
                    <Text fontSize="13px" color="gray.500">
                        {Math.min((currentPage - 1) * pageSize + 1, sortedFilteredParticipants.length)}–{Math.min(currentPage * pageSize, sortedFilteredParticipants.length)} z {sortedFilteredParticipants.length}
                        {search && participants.length !== sortedFilteredParticipants.length && ` (filtr z ${participants.length})`}
                    </Text>

                    <HStack spacing={1}>
                        <Text fontSize="13px" color="gray.500" mr={1}>Pokaż:</Text>
                        {[10, 25, 50].map(size => (
                            <Button key={size} size="xs"
                                onClick={() => { setPageSize(size); setCurrentPage(1); }}
                                bg={pageSize === size ? 'brand.500' : 'white'}
                                color={pageSize === size ? 'white' : 'gray.600'}
                                border="1px solid" borderColor={pageSize === size ? 'brand.500' : 'gray.200'}
                                borderRadius="0" fontWeight="500" minW="32px"
                                _hover={{ bg: pageSize === size ? 'brand.500' : 'gray.50' }}>
                                {size}
                            </Button>
                        ))}
                    </HStack>

                    <HStack spacing={1}>
                        <IconButton icon={<FiChevronLeft />} size="xs" aria-label="Poprzednia strona"
                            isDisabled={currentPage === 1}
                            onClick={() => setCurrentPage(p => p - 1)}
                            variant="ghost" color="gray.600" />
                        {Array.from({ length: totalPages }, (_, i) => i + 1)
                            .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                            .reduce((acc, p, i, arr) => {
                                if (i > 0 && p - arr[i - 1] > 1) acc.push('…');
                                acc.push(p);
                                return acc;
                            }, [])
                            .map((p, i) => p === '…' ? (
                                <Text key={`ell-${i}`} fontSize="13px" color="gray.400" px={1} userSelect="none">…</Text>
                            ) : (
                                <Button key={p} size="xs"
                                    onClick={() => setCurrentPage(p)}
                                    bg={currentPage === p ? 'brand.500' : 'white'}
                                    color={currentPage === p ? 'white' : 'gray.600'}
                                    border="1px solid" borderColor={currentPage === p ? 'brand.500' : 'gray.200'}
                                    borderRadius="0" fontWeight="500" minW="32px"
                                    _hover={{ bg: currentPage === p ? 'brand.500' : 'gray.50' }}>
                                    {p}
                                </Button>
                            ))}
                        <IconButton icon={<FiChevronRight />} size="xs" aria-label="Następna strona"
                            isDisabled={currentPage === totalPages || totalPages === 0}
                            onClick={() => setCurrentPage(p => p + 1)}
                            variant="ghost" color="gray.600" />
                    </HStack>
                </Flex>
            )}

            {/* Delete confirmation dialog */}
            <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={onClose} isCentered>
                <AlertDialogOverlay bg="blackAlpha.300">
                    <AlertDialogContent borderRadius="0" maxW="660px" mx={4} textAlign="center" px={6} py={6} position="relative">
                        <CloseButton position="absolute" top="16px" right="16px" onClick={onClose} color="black" />
                        <AlertDialogHeader fontSize="20px" fontWeight="bold" color="black" pb={2} textAlign="left">
                            {t('event.participants.delete')}
                        </AlertDialogHeader>
                        <AlertDialogBody fontSize="16px" color="black" whiteSpace="pre-line" py="48px">
                            {selectedParticipant && (
                                <>
                                    <Text fontWeight={600} fontSize="22px">{t('event.participants.confirm')}</Text>
                                    <Box mt={4}>{t('event.participant.delete.confirmation', selectedParticipant)}</Box>
                                </>
                            )}
                        </AlertDialogBody>
                        <AlertDialogFooter justifyContent="center" pt={6}>
                            <Button ref={cancelRef} onClick={onClose} borderRadius="0" border="1px solid #0A4725"
                                bg="white" color="#0A4725" px={8} py={5} fontWeight="500" _hover={{ bg: "#f0f0f0" }}>
                                {t('cancel')}
                            </Button>
                            <Button onClick={handleDelete} borderRadius="0" bg="#0A4725" color="white" ml={4} px={8} py={5}
                                fontWeight="500"
                                rightIcon={
                                    <svg width="19" height="20" viewBox="0 0 19 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M16.5 5L15.633 17.142C15.5971 17.6466 15.3713 18.1188 15.0011 18.4636C14.6309 18.8083 14.1439 19 13.638 19H5.362C4.85614 19 4.36907 18.8083 3.99889 18.4636C3.6287 18.1188 3.40292 17.6466 3.367 17.142L2.5 5M7.5 9V15M11.5 9V15M12.5 5V2C12.5 1.73478 12.3946 1.48043 12.2071 1.29289C12.0196 1.10536 11.7652 1 11.5 1H7.5C7.23478 1 6.98043 1.10536 6.79289 1.29289C6.60536 1.48043 6.5 1.73478 6.5 2V5M1.5 5H17.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                }
                                _hover={{ bg: "#0c5a32" }}>
                                {t('delete')}
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </Box>
    );
};

export default EventParticipantsCardList;
